import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Text from '../text';
import {
  addUtmParams,
  tryWebShare,
  openGmailCompose,
  openOutlookCompose,
  openWhatsAppShare,
  buildMailtoURI,
  openMailtoSafely,
} from '../../helpers/mailto';
import { track } from '../../services/analytics';
import IsMobileDevice from '../Generic/IsMobileDevice';

// MUI icons
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import IosShareIcon from '@mui/icons-material/IosShare';
import EmailIcon from '@mui/icons-material/Email';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUBJECT = 'iAngels investment opportunity';

const ShareByEmail = ({ companyName, companyUrl, investorName }) => {
  const [referralName, setReferralName] = useState('');
  const [referralEmail, setReferralEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');

  const isNameValid = referralName.trim() !== '';
  const isEmailValid = EMAIL_REGEX.test(referralEmail.trim());
  const isMobile = IsMobileDevice();

  const resetForm = () => {
    setReferralName('');
    setReferralEmail('');
    setEmailError('');
    setNameError('');
  };

  const handleValidation = (mode = 'both') => {
    // mode: 'name' | 'email' | 'both'
    if (mode === 'name') {
      if (!isNameValid) {
        setNameError('Referral name required');
        return false;
      }
      return true;
    }

    if (mode === 'email') {
      if (!isEmailValid) {
        setEmailError('Enter a valid email');
        return false;
      }
      return true;
    }

    // both
    let ok = true;
    if (!isNameValid) {
      setNameError('Referral name required');
      ok = false;
    }
    if (!isEmailValid) {
      setEmailError('Enter a valid email');
      ok = false;
    }
    return ok;
  };

  const callTrack = (method) => {
    try {
      track('Share Opportunity', {
        startup: companyName,
        method,
        investorName: investorName || null,
      });
    } catch (e) {
      // ignore tracking errors
    }
  };

  // Build a consistent utm_content once
  const content = (companyName || 'iangels').replace(/\s+/g, '-').toLowerCase();

  const buildTrackedUrl = (utmMedium) => {
    // utmMedium examples: 'email' | 'whatsapp' | 'webshare'
    return addUtmParams(companyUrl || '', {
      utm_source: 'friend-share',
      utm_medium: utmMedium,
      utm_campaign: 'share',
      utm_content: content,
    });
  };

  const buildBody = (utmMedium) => {
    const trackedUrl = buildTrackedUrl(utmMedium);
    return `Hi ${referralName},\n\nI just invested in ${companyName}, and thought this opportunity might interest you as well. You can learn more about the company and the deal terms through the iAngels investment memo here:\n\n${trackedUrl}\n\nor get in touch with the iAngels team directly via myteam@iangels.com.\n\nBest,\n${investorName}`;
  };

  const handleWebShare = async () => {
    // requires only name
    if (!handleValidation('name')) return;
    callTrack('webshare');

    const bodyText = buildBody('webshare');
    const ok = await tryWebShare({
      title: SUBJECT,
      text: bodyText,
      url: buildTrackedUrl('webshare'),
    });

    if (ok) resetForm();
    return ok;
  };

  const handleOpenGmail = () => {
    // requires name + valid email
    if (!handleValidation('both')) return;
    callTrack('google');

    const bodyText = buildBody('email');
    openGmailCompose({ to: referralEmail, subject: SUBJECT, body: bodyText });

    resetForm();
  };

  const handleOpenOutlook = () => {
    // requires name + valid email
    if (!handleValidation('both')) return;
    callTrack('outlook');

    const bodyText = buildBody('email');
    openOutlookCompose({ to: referralEmail, subject: SUBJECT, body: bodyText });

    resetForm();
  };

  const handleWhatsApp = () => {
    // requires only name
    if (!handleValidation('name')) return;
    callTrack('whatsapp');

    const bodyText = buildBody('whatsapp');
    openWhatsAppShare({ text: bodyText });

    resetForm();
  };

  const handleMailto = () => {
    // requires name + valid email
    if (!handleValidation('both')) return;
    callTrack('mailto');

    const bodyText = buildBody('email');
    const mailtoUrl = buildMailtoURI({ to: referralEmail, subject: SUBJECT, body: bodyText });
    openMailtoSafely(mailtoUrl);

    resetForm();
  };

  // Shared button styling
  const iconBtnSx = {
    flex: '0 0 auto',
    minHeight: 32,
    minWidth: 40,
    padding: '6px 10px',
    color: 'currentColor',
    borderColor: 'currentColor',
    '&:hover': {
      borderColor: 'currentColor',
    },
  };

  return (
    <Stack spacing={1} style={{ margin: '24px 0 0', width: '100%' }}>
      <Text
        text="Know someone who might find this interesting?"
        classes="f-f-g f-w-400 f-size-14 g-c"
        style={{ textAlign: 'left' }}
      />
      <Text
        text="Share this opportunity"
        classes="f-f-tgf f-f-500 f-size-16 lh-20 g4-c"
        style={{ textAlign: 'left' }}
      />

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1}
        justifyContent="center"
        sx={{ width: '100%', alignItems: 'center', flexWrap: 'nowrap' }}
      >
        <TextField
          label="Name"
          variant="standard"
          value={referralName}
          onChange={(e) => {
            setReferralName(e.target.value);
            setNameError('');
          }}
          error={!!nameError}
          helperText={nameError}
          FormHelperTextProps={{ style: { fontSize: 11 } }}
          fullWidth
          sx={{
            flex: '1 1 30%',
            minWidth: 0,

            // underline default
            '& .MuiInput-root.MuiInput-underline:before': {
              borderBottomColor: 'currentColor',
            },

            // underline hover
            '& .MuiInput-root.MuiInput-underline:hover:before': {
              borderBottomColor: 'currentColor',
            },

            // underline focused
            '& .MuiInput-root.MuiInput-underline:after': {
              borderBottomColor: 'currentColor',
            },
          }}
          InputLabelProps={{ className: 'f-f-g f-w-400 f-size-14 g4-c' }}
          InputProps={{
            // This class must be on the underline/root element so currentColor works
            className: 'f-f-g f-w-400 f-size-14 g3-c',
          }}
          inputProps={{
            // This class is applied to the actual <input> text
            className: 'f-f-g f-w-400 f-size-14 g4-c',
          }}
        />

        <TextField
          label="Email"
          variant="standard"
          value={referralEmail}
          onChange={(e) => {
            setReferralEmail(e.target.value);
            setEmailError('');
          }}
          error={!!emailError}
          helperText={emailError}
          FormHelperTextProps={{ style: { fontSize: 11 } }}
          fullWidth
          sx={{
            flex: '1 1 30%',
            minWidth: 0,

            // underline default
            '& .MuiInput-root.MuiInput-underline:before': {
              borderBottomColor: 'currentColor',
            },

            // underline hover
            '& .MuiInput-root.MuiInput-underline:hover:before': {
              borderBottomColor: 'currentColor',
            },

            // underline focused
            '& .MuiInput-root.MuiInput-underline:after': {
              borderBottomColor: 'currentColor',
            },
          }}
          InputLabelProps={{ className: 'f-f-g f-w-400 f-size-14 g4-c' }}
          InputProps={{
            // This class must be on the underline/root element so currentColor works
            className: 'f-f-g f-w-400 f-size-14 g3-c',
          }}
          inputProps={{
            // This class is applied to the actual <input> text
            className: 'f-f-g f-w-400 f-size-14 g4-c',
          }}
        />

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            '&&': { mt: isMobile ? 2 : 0 },
          }}
        >
          {isMobile ? (
            <Tooltip title="Email" arrow>
              <span>
                <Button
                  variant="outlined"
                  className="g4-c"
                  onClick={handleMailto}
                  sx={iconBtnSx}
                  aria-label="Share via Email"
                >
                  <EmailIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Gmail" arrow>
                <span>
                  <Button
                    variant="outlined"
                    className="g4-c"
                    onClick={handleOpenGmail}
                    sx={iconBtnSx}
                    aria-label="Share via Gmail"
                  >
                    <EmailIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Outlook" arrow>
                <span>
                  <Button
                    variant="outlined"
                    className="g4-c"
                    onClick={handleOpenOutlook}
                    sx={iconBtnSx}
                    aria-label="Share via Outlook"
                  >
                    <MailOutlineIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          <Tooltip title="WhatsApp" arrow>
            <span>
              <Button
                variant="outlined"
                className="g4-c"
                onClick={handleWhatsApp}
                sx={iconBtnSx}
                aria-label="Share via WhatsApp"
              >
                <WhatsAppIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="More options" arrow>
            <span>
              <Button
                variant="outlined"
                className="g4-c"
                onClick={handleWebShare}
                sx={iconBtnSx}
                aria-label="More sharing options"
              >
                <IosShareIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ShareByEmail;
